import { Home, Play, CalendarCheck, Medal, MessageCircle, User } from 'lucide-react';
import { useT, type MessageKey } from '../../i18n';

export type MobileTab = 'home' | 'play' | 'reservations' | 'matches' | 'messages' | 'profile';

// THE TABLE CARRIES A KEY, NOT A WORD (#310). It is module-level data, so
// there is no `useT()` to call here; the label is resolved where it is
// rendered. `labelKey` is typed as a MessageKey, so a tab naming a key the
// catalog does not have fails to compile rather than rendering the key.
export const MOBILE_TABS: { id: MobileTab; labelKey: MessageKey; icon: any }[] = [
  { id: 'home', labelKey: 'mobile.tab.home', icon: Home },
  { id: 'play', labelKey: 'mobile.tab.play', icon: Play },
  { id: 'reservations', labelKey: 'mobile.tab.reservations', icon: CalendarCheck },
  { id: 'matches', labelKey: 'mobile.tab.matches', icon: Medal },
  { id: 'messages', labelKey: 'mobile.tab.messages', icon: MessageCircle },
  { id: 'profile', labelKey: 'mobile.tab.profile', icon: User },
];

export interface MobileTabBarProps {
  value: MobileTab;
  onChange: (tab: MobileTab) => void;
  /** Counters shown as a lime dot-badge on a tab (e.g. unread messages). */
  badges?: Partial<Record<MobileTab, number>>;
  /** Placement in the player shell's grid (`ps-nav`). */
  className?: string;
}

// Bottom tab bar — the app's primary navigation: Hjem · Spill (the centre:
// Om · Book · Aktiviteter · Matcher) · Reservasjoner · Kamper (turneringer +
// seriespill) · Meldinger (the inbox, with an unread badge) · Profil. Six items
// was a deliberate product call (messaging earned its own tab); anything more
// belongs inside a tab.
// ── ONE NAVIGATION, THREE SHAPES (desktop brief, 2026-10-16) ──────────
//
// The same six buttons at every width, restyled by the APP's width (container
// queries — see `.player-shell`): the phone's bottom bar; a vertical icon rail
// from 768 px; a labelled sidebar list from 1200 px, where the active tab is a
// full lime row instead of a lime pill around the icon. Andreas' bar is the
// compact shape, untouched.
export function MobileTabBar({ value, onChange, badges, className = '' }: MobileTabBarProps) {
  const t = useT();
  return (
    <nav
      // `pb-[18px]` clears the phone's home indicator; the rail has none.
      className={
        'flex items-stretch bg-[rgba(255,255,255,0.92)] backdrop-blur-md border-t border-[#e7eae3] pb-[18px] ' +
        '@rail:flex-col @rail:justify-start @rail:gap-1 @rail:px-2 @rail:py-2 @rail:pb-2 ' +
        '@rail:bg-transparent @rail:backdrop-blur-none @rail:border-t-0 @wide:px-3 ' +
        className
      }
      aria-label={t('mobile.tab.navLabel')}
    >
      {MOBILE_TABS.map((tab) => {
        const active = tab.id === value;
        const Icon = tab.icon;
        const badge = badges?.[tab.id] || 0;
        const label = t(tab.labelKey);
        return (
          <button
            key={tab.id}
            onClick={() => onChange(tab.id)}
            className={
              'flex-1 min-w-0 flex flex-col items-center justify-center gap-0.5 pt-2.5 pb-1 rounded-2xl ' +
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0f1611] ' +
              '@rail:flex-none @rail:py-2 @rail:hover:bg-[#eef0ea] ' +
              '@wide:flex-row @wide:justify-start @wide:gap-3 @wide:px-3 @wide:py-2.5 ' +
              (active ? '@wide:bg-[#c2f02f] @wide:hover:bg-[#c2f02f]' : '')
            }
            style={{ color: active ? '#0f1611' : 'var(--ink-3)' }}
            aria-current={active ? 'page' : undefined}
            aria-label={badge > 0 ? t('mobile.tab.unreadAria', { label, count: badge }) : undefined}
          >
            <span
              className={
                'relative flex items-center justify-center rounded-full transition-colors w-9 h-6 flex-shrink-0 ' +
                (active ? 'bg-[#c2f02f] @wide:bg-transparent' : '') +
                ' @wide:w-5 @wide:h-5'
              }
            >
              <Icon size={17} strokeWidth={active ? 2.4 : 2} />
              {badge > 0 && (
                <span
                  className="absolute font-mono font-bold rounded-full flex items-center justify-center"
                  style={{
                    top: -4,
                    right: -6,
                    minWidth: 16,
                    height: 16,
                    padding: '0 4px',
                    fontSize: 9,
                    background: '#0f1611',
                    color: '#c2f02f',
                    border: '2px solid #fff',
                  }}
                  data-testid={`tab-badge-${tab.id}`}
                >
                  {badge > 99 ? '99+' : badge}
                </span>
              )}
            </span>
            <span className="text-[9px] font-semibold truncate max-w-full @rail:text-[9px] @rail:tracking-tight @wide:text-[14px] @wide:tracking-normal">
              {label}
            </span>
          </button>
        );
      })}
    </nav>
  );
}
